import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  memo,
  Suspense,
} from 'react';
import to from 'await-to-js';
import { IFunnelRaw, IPasswordProtectedFunnelData } from './types/funnels';
import {
  getFunnelPublicRequest,
  isFunnelPasswordProtected,
} from './api/public/funnels';
import { autofixFunnel } from './autofixers/autofixFunnel';
import {
  PlayerContainerFromFileId,
  DetachableStoreProviderForPlayer,
  VideoItemsContainerProvider,
  PlayerComponentsProvider,
  PlayerMode,
  getVideoAspectRatio,
  IPasswordProtection,
  IPasswordProtectedPlayerVideo,
} from '@voomly/player/funnel-deps';
import {
  IPlayerVideo,
  IPlayerTemplate,
  IPlayerWithVideo,
  IRatio,
  IPlaybackSettings,
} from '@voomly/player/funnel-deps';
import { Ratio, Loader } from '@voomly/ui/funnel-deps';
import omit from 'lodash-es/omit';
import { prepareFunnelSubtitles } from '../lib/utils/prepareFunnelSubtitles';
import { PlayerExternalAPI } from '@voomly/player/funnel-deps';

// Lazy components
const LazyUnlockFunnel = React.lazy(
  () => import('./components/UnlockFunnel/UnlockFunnel')
);

interface IFunnelPlayerProps {
  ratio: IRatio;
  funnelId: string;
  onLoaded?: (playerConfig: IPlayerTemplate, file: IPlayerVideo) => void;
  onLoadedPasswordForm?: (
    playerConfig: IPasswordProtection,
    file: IPasswordProtectedPlayerVideo
  ) => void;
  isRememberDismissEnabled: boolean;
  extraContent?: React.ReactNode;
  playerMode: PlayerMode;
  playerExternalAPI?: PlayerExternalAPI;
  disableShortcuts?: boolean;
}

const FunnelsPlayerComponent = ({
  ratio,
  funnelId,
  onLoaded,
  onLoadedPasswordForm,
  playerMode,
  isRememberDismissEnabled,
  extraContent,
  playerExternalAPI,
  disableShortcuts,
}: IFunnelPlayerProps) => {
  const [playbackSettings, setPlaybackSettings] = useState<IPlaybackSettings>({
    currentTime: 0,
  });
  const [funnel, setFunnel] = useState<IFunnelRaw | undefined>();
  const [passwordProtectedFunnelData, setPasswordProtectedFunnelData] =
    useState<IPasswordProtectedFunnelData | undefined>(undefined);
  const [currentFunnelNodeIdx, setCurrentFunnelNodeIdx] = useState<
    number | undefined
  >();
  const [error, setError] = useState<{ message: string } | undefined>(
    undefined
  );

  const forceAspectRatioRef = useRef<number | undefined>();

  const getCurrentNodeIdx = useCallback((funnel: IFunnelRaw) => {
    let currentFunnelNodeIndex = 0;

    if (funnel.startNodeId) {
      const idx = funnel.nodes.findIndex(
        (node) => node.id === funnel.startNodeId
      );

      if (~idx) {
        currentFunnelNodeIndex = idx;
      }
    }

    return currentFunnelNodeIndex;
  }, []);

  const fetchFunnel = useCallback(async () => {
    if (!funnelId) {
      setError({
        message: 'Interactive video is missing',
      });
      return;
    }

    setError(undefined);
    setFunnel(undefined);

    const [err, response] = await to(getFunnelPublicRequest(funnelId));
    if (response) {
      const { data } = response;

      if (isFunnelPasswordProtected(data)) {
        setPasswordProtectedFunnelData(data);
        onLoadedPasswordForm?.(data.player, data.video);
      } else {
        setFunnel(autofixFunnel(data));
        setCurrentFunnelNodeIdx(getCurrentNodeIdx(data));
      }
      return;
    }

    setError({
      message: err?.message ?? 'Unable to start the interactive video',
    });
  }, [funnelId, onLoadedPasswordForm, getCurrentNodeIdx]);

  useEffect(() => {
    void fetchFunnel();
  }, [fetchFunnel]);

  const handleNavigateToOtherNodeRequest = useCallback(
    (nodeId: string, timestamp?: number) => {
      if (!funnel) {
        return;
      }

      const newIdx = funnel.nodes.findIndex((node) => node.id === nodeId);
      if (!~newIdx) {
        console.log('Answer led nowhere, do nothing');
        return;
      }

      setPlaybackSettings({
        currentTime: timestamp || 0,
        forceAutoPlay: true,
        forceAspectRatio: forceAspectRatioRef.current,
      });
      setCurrentFunnelNodeIdx(newIdx);
    },
    [funnel]
  );

  // This ensures player won't change its size when user changes the video
  // Player always stays at the size of the first video
  const handleVideoLoaded = useCallback(
    (file: IPlayerVideo) => {
      // That's not the first video in the funnel - player dimensions should stay as the first video requested
      if (forceAspectRatioRef.current || !funnel) {
        return;
      }

      // Yes, forceAspectRatio lives outside of React lifecycle (not in state nor props).
      // Apparently player goes crazy if you try to switch playbackSettings during initialization
      // steps; we lock aspectRatio so other videos in the funnel plays in the same player's dimensions,
      // so we force the next video to have these dimensions (without touching the current)
      forceAspectRatioRef.current = getVideoAspectRatio(file);

      onLoaded?.(funnel.playerConfig, file);
    },
    [onLoaded, funnel]
  );

  const renderLoader = useCallback(() => {
    return (
      <Loader
        color={
          funnel?.playerConfig?.skin.bgColorEnabled
            ? funnel.playerConfig.skin.bgColor
            : undefined
        }
      />
    );
  }, [funnel]);

  const handleUnlockFunnel = useCallback(
    (password: string, unlockedFunnel: IFunnelRaw) => {
      setFunnel(unlockedFunnel);
      setPasswordProtectedFunnelData(undefined);
      setCurrentFunnelNodeIdx(getCurrentNodeIdx(unlockedFunnel));

      if (playerExternalAPI) {
        playerExternalAPI.fromEmitter.timelineUnlock.emit({
          password,
        });
      }
    },
    [playerExternalAPI, getCurrentNodeIdx]
  );

  const renderPasswordForm = () => {
    if (!passwordProtectedFunnelData) {
      return null;
    }

    return (
      <Ratio ratio={ratio}>
        <Suspense fallback={renderLoader()}>
          <LazyUnlockFunnel
            funnelId={funnelId}
            lockedPlayer={passwordProtectedFunnelData.player}
            lockedFile={passwordProtectedFunnelData.video}
            onUnlock={handleUnlockFunnel}
          />
        </Suspense>
      </Ratio>
    );
  };

  if (funnel && currentFunnelNodeIdx !== undefined) {
    const currentNode = funnel.nodes[currentFunnelNodeIdx];

    const mergedSubtitles = prepareFunnelSubtitles(
      funnel.subtitles,
      currentNode.player.subtitles
    );

    const playerConfig: IPlayerWithVideo = {
      ...funnel.playerConfig,
      attachedPlayerTemplates: funnel.attachedPlayerTemplates,
      videoId: currentNode.videoId,
      thumbnail: funnel.thumbnail,
      timelineItems: currentNode.timelineItems,
      thumbnailItems: funnel.thumbnailItems,
      shareVideo: funnel.shareVideo,
      subtitles: mergedSubtitles,
      ...omit(currentNode.player, 'subtitles'),
    };

    return (
      <VideoItemsContainerProvider>
        <PlayerComponentsProvider>
          <DetachableStoreProviderForPlayer
            timelineItems={currentNode.timelineItems}
            thumbnailItems={playerConfig.thumbnailItems}
          >
            <PlayerContainerFromFileId
              ratio={ratio}
              fileId={currentNode.videoId}
              playerMode={playerMode}
              playbackSettings={playbackSettings}
              onNavigateToOtherNodeRequest={handleNavigateToOtherNodeRequest}
              onLoaded={handleVideoLoaded}
              playerConfig={playerConfig}
              isRememberDismissEnabled={isRememberDismissEnabled}
              extraContent={extraContent}
              funnelId={funnel?.id}
              playerExternalAPI={playerExternalAPI}
              currentFunnelNodeId={currentNode.id}
              disableShortcuts={disableShortcuts}
            />
          </DetachableStoreProviderForPlayer>
        </PlayerComponentsProvider>
      </VideoItemsContainerProvider>
    );
  }

  if (passwordProtectedFunnelData) {
    return renderPasswordForm();
  }

  if (error) {
    return <h3>Error: {error}</h3>;
  }

  return <Ratio ratio={ratio}>{renderLoader()}</Ratio>;
};

export const FunnelsPlayer = memo(FunnelsPlayerComponent);
